import api from "./api";
import { AxiosResponse } from "axios";

export interface DashboardStats {
  merchant_code: string;
  total_deposits: number;
  total_withdrawals: number;
  total_transactions: number;
  net_balance: number;
  by_operator: {
    method: string;
    currency: string;
    action: string;
    total_amount: number;
    total_transactions: number;
    successful_transactions?: number;
    failed_transactions?: number;
    pending_transactions?: number;
    submitted_transactions?: number;
  }[];
}

export interface OperatorStats {
  merchant_code: string;
  operator_stats: {
    method: string;
    currency: string;
    action: string;
    total_transactions: number;
    successful: number;
    failed: number;
    pending: number;
    submitted: number;
    total_amount: number;
  }[];
}

export interface BalanceData {
  merchant_code: string;
  balances: {
    method: string;
    currency: string;
    closing_balance_withdrawal?: number;
    total_withdrawal?: number;
    withdrawal_commission_rate?: number;
    withdrawal_commission_amount?: number;
    realtime_withdrawal: number;
    closing_balance_deposit?: number;
    total_deposit?: number;
    deposit_commission_rate?: number;
    deposit_commission_amount?: number;
    realtime_deposit: number;
    reserved_deposit?: number;
    reserved_withdrawal?: number;
    available?: number;
    in_flight?: number;
    total?: number;
  }[];
}

export interface HourlyChartData {
  hour: number;
  total_count: number;
  successful_count: number;
  failed_count: number;
  pending_count: number;
  submitted_count: number;
  deposits: number;
  withdrawals: number;
}

export interface DailyStats {
  date: string;
  total_transactions: number;
  successful_transactions: number;
  failed_transactions: number;
  total_amount: number;
  success_rate: number;
}

export interface DailyStatsResponse {
  period: { start_date: string; end_date: string; days: number };
  daily_stats: DailyStats[];
}

export interface RecentTransaction {
  id: number;
  merchant_code: string;
  method: string;
  amount: number;
  currency: string;
  action: string;
  status: string;
  updated_at: string;
}

export const dashboardService = {
  async getStats(): Promise<DashboardStats> {
    try {
      const response: AxiosResponse<DashboardStats> = await api.get("/dashboard/stats");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  async getOperatorStats(): Promise<OperatorStats> {
    try {
      const response: AxiosResponse<OperatorStats> = await api.get("/dashboard/operator-stats");
      return response.data;
    } catch (error: any) {
      throw error;
    }
  },

  async getBalances(): Promise<BalanceData> {
    const response: AxiosResponse<BalanceData> = await api.get("/dashboard/balances");
    return response.data;
  },

  async getHourlyChart(period: string = "today", currency?: string): Promise<HourlyChartData[]> {
    const params = new URLSearchParams({ period });
    if (currency) params.set("currency", currency);
    const response: AxiosResponse<HourlyChartData[]> = await api.get(
      `/dashboard/transactions/chart?${params.toString()}`
    );
    return response.data;
  },

  async getDailyStats(days: number = 7, currency?: string): Promise<DailyStatsResponse> {
    const params = new URLSearchParams({ days: String(days) });
    if (currency) params.set("currency", currency);
    const response: AxiosResponse<DailyStatsResponse> = await api.get(
      `/dashboard/stats/daily?${params.toString()}`
    );
    return response.data;
  },

  async getRecentTransactions(): Promise<RecentTransaction[]> {
    const response: AxiosResponse<RecentTransaction[]> = await api.get("/dashboard/transactions/recent");
    return response.data;
  },
};
